import { GraduationCap, Users, Globe, Calendar, MapPin, HeartHandshake, Layers, Briefcase, Flag, ChevronDown } from "lucide-react";
import { useState } from 'react';
import { useTranslation } from "react-i18next";

export default function AboutKonaspiXII() {
    const { t } = useTranslation('public');
    const [isExpanded, setIsExpanded] = useState(false);
    const [showMoreInfo, setShowMoreInfo] = useState(false);

    const toggleText = () => setIsExpanded(!isExpanded);
    const toggleMoreInfo = () => setShowMoreInfo(!showMoreInfo);

    const activityIcons = [
        { key: "convention", icon: GraduationCap },
        { key: "conference", icon: Globe },
        { key: "fgd", icon: Layers },
        { key: "exhibition", icon: Briefcase },
        { key: "sports", icon: Users },
        { key: "ladies", icon: HeartHandshake },
        { key: "tour", icon: MapPin },
    ];

    return (
        <section id="about" className="relative py-16 md:py-24 overflow-hidden bg-white dark:bg-gray-900">
            {/* Ornamen pojok diringankan opacity-nya agar lebih subtle */}
            <img src="/ornamen/ukiran-corner.png" alt="Ukiran Bali" className="absolute top-0 left-0 w-40 md:w-56 opacity-50 pointer-events-none z-0" />
            <img src="/ornamen/ukiran-corner.png" alt="Ukiran Bali" className="absolute top-0 right-0 w-40 md:w-56 scale-x-[-1] opacity-50 pointer-events-none z-0" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* SECTION: LATAR BELAKANG */}
                <div className="max-w-4xl mx-auto text-center mb-16">
                    <h2 className="text-3xl md:text-4xl font-bold text-blue-900 dark:text-blue-400 mb-6 tracking-tight">
                        {t('about.background_title')}
                    </h2>
                    <div className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed text-justify md:text-center space-y-4">
                        <p>{t('about.background_p1')}</p>
                        {isExpanded && (
                            <div className="space-y-4 animate-in fade-in duration-300">
                                <p>{t('about.background_p2')}</p>
                                <p>{t('about.background_p3')}</p>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={toggleText}
                        className="mt-6 inline-flex items-center justify-center gap-2 text-blue-600 dark:text-blue-400 font-semibold hover:text-blue-800 transition-colors"
                    >
                        <span>{isExpanded ? t('about.show_less') : t('about.show_more')}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`} />
                    </button>
                </div>

                {/* Grid Layout untuk Tujuan & Tema (Tanpa kotak-kotak card) */}
                <div className="grid md:grid-cols-2 gap-12 lg:gap-20 mb-12">
                    {/* TUJUAN */}
                    <div>
                        <h3 className="text-2xl font-bold text-blue-900 dark:text-blue-400 mb-6">
                            {t('about.objectives_title')}
                        </h3>
                        <div className="space-y-5">
                            {(t('about.objectives', { returnObjects: true }) as string[]).map((obj, index) => (
                                <div key={index} className="flex items-start gap-4">
                                    <div className="w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                                        {index + 1}
                                    </div>
                                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{obj}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* TEMA */}
                    <div>
                        <h3 className="text-2xl font-bold text-blue-900 dark:text-blue-400 mb-6">
                            {t('about.theme_title')}
                        </h3>
                        <p className="text-xl font-bold text-gray-900 dark:text-white mb-4 leading-snug">
                            "{t('tagline.main')}"
                        </p>
                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-justify">
                            {t('about.theme_desc')}
                        </p>
                    </div>
                </div>

                {/* SHOW MORE WRAPPER */}
                <div className="text-center pt-8 mb-12">
                    <button
                        onClick={toggleMoreInfo}
                        className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-md transition-all duration-300"
                    >
                        <span>{showMoreInfo ? t('about.show_less') : t('about.show_more')}</span>
                        <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${showMoreInfo ? "rotate-180" : ""}`} />
                    </button>
                </div>

                {/* DETAIL SELENGKAPNYA */}
                <div className={`transition-all duration-700 overflow-hidden ${showMoreInfo ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"}`}>
                    
                    {/* ALASAN & TAGLINE */}
                    <div className="grid md:grid-cols-2 gap-12 lg:gap-20 mb-20">
                        <div>
                            <h3 className="text-2xl font-bold text-blue-900 dark:text-blue-400 mb-6">
                                {t('about.reasons_title')}
                            </h3>
                            <div className="space-y-5">
                                {(t('about.reasons', { returnObjects: true }) as string[]).map((r, i) => (
                                    <div key={i} className="flex items-start gap-4">
                                        <Flag className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{r}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div>
                            <h3 className="text-2xl font-bold text-blue-900 dark:text-blue-400 mb-2">
                                {t('about.tagline_title')}
                            </h3>
                            <p className="text-blue-600 dark:text-blue-400 font-medium mb-6">
                                {t('about.tagline_main')}
                            </p>
                            <div className="space-y-6">
                                {['innovation', 'synergy', 'generation'].map((pillar) => {
                                    const rawText = t(`about.tagline_items.${pillar}`);
                                    const [title, desc] = rawText.includes(':') ? rawText.split(':') : [pillar.toUpperCase(), rawText];
                                    return (
                                        <div key={pillar}>
                                            <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-1">{title}</h4>
                                            <p className="text-gray-700 dark:text-gray-300">{desc}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* KEGIATAN */}
                    <div className="mb-16 max-w-5xl mx-auto text-center">
                        <h3 className="text-2xl font-bold text-blue-900 dark:text-blue-400 mb-10">
                            {t('about.activities_title')}
                        </h3>
                        <div className="flex flex-wrap justify-center gap-x-6 gap-y-10 sm:gap-12">
                            {activityIcons.map((a) => (
                                <div key={a.key} className="flex flex-col items-center w-[40%] sm:w-[25%] md:w-[20%] lg:w-[18%]">
                                    <div className="w-16 h-16 rounded-full bg-blue-50 dark:bg-gray-800 flex items-center justify-center mb-4 text-blue-600 dark:text-blue-400">
                                        <a.icon className="w-8 h-8" />
                                    </div>
                                    <p className="font-semibold text-gray-900 dark:text-white text-center">
                                        {t(`about.activities.${a.key}`)}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* TANGGAL & TEMPAT */}
                    <div className="max-w-5xl mx-auto">
                        <h3 className="text-2xl font-bold text-blue-900 dark:text-blue-400 mb-10 text-center">
                            {t('about.location_title')}
                        </h3>
                        <div className="grid md:grid-cols-2 gap-8">
                            {/* Conference */}
                            <div className="p-8 border border-gray-200 dark:border-gray-800 rounded-2xl bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm">
                                <div className="flex flex-col items-start gap-3 mb-6 pb-4 border-b border-gray-100 dark:border-gray-800">
                                    <span className="text-xl font-bold text-gray-900 dark:text-white leading-tight">{t('about.location_conference')}</span>
                                    <span className="text-xs font-bold uppercase tracking-wider text-blue-600 bg-blue-50 dark:bg-blue-900/30 px-3 py-1.5 rounded-full">{t('hero.conference.type')}</span>
                                </div>
                                <div className="space-y-5 text-gray-700 dark:text-gray-300">
                                    <div className="flex items-start gap-4">
                                        <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                                        <p>{t('hero.conference.date')}</p>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                                        <div>
                                            <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">{t('about.location_place')}</span>
                                            <p>{t('hero.conference.location')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Convention */}
                            <div className="p-8 border border-gray-200 dark:border-gray-800 rounded-2xl bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm">
                                <div className="flex flex-col items-start gap-3 mb-6 pb-4 border-b border-gray-100 dark:border-gray-800">
                                    <span className="text-xl font-bold text-gray-900 dark:text-white leading-tight">{t('about.location_convention')}</span>
                                    <span className="text-xs font-bold uppercase tracking-wider text-green-600 bg-green-50 dark:bg-green-900/30 px-3 py-1.5 rounded-full">{t('hero.convention.type')}</span>
                                </div>
                                <div className="space-y-5 text-gray-700 dark:text-gray-300">
                                    <div className="flex items-start gap-4">
                                        <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                                        <p>{t('hero.convention.date')}</p>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                                        <div>
                                            <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">{t('about.location_place')}</span>
                                            <p>{t('hero.convention.location')}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <Users className="w-5 h-5 text-gray-400 mt-0.5" />
                                        <div>
                                            <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">{t('about.location_participants')}</span>
                                            <p>{t('about.location_participants_desc')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                </div>
            </div>
            
            <img src="/ornamen/ukiran-corner-bottom.png" alt="Ukiran Bali" className="absolute bottom-0 left-0 w-40 md:w-56 opacity-50 pointer-events-none z-0" />
            <img src="/ornamen/ukiran-corner-bottom.png" alt="Ukiran Bali" className="absolute bottom-0 right-0 w-40 md:w-56 scale-x-[-1] opacity-50 pointer-events-none z-0" />
        </section>
    );
}